import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGIN_LOG } from "@/constants/testIds";
import { toast } from "@/lib/notify";
import { ShieldAlert, RefreshCw, KeyRound, Hash } from "lucide-react";

function formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

export default function LoginLogSection() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "administrador";

  const [logs, setLogs] = useState([]);
  const [result, setResult] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (result) params.set("result", result);
      if (emailFilter.trim()) params.set("email", emailFilter.trim());
      params.set("limit", "200");
      const { data } = await api.get(`/login-logs?${params.toString()}`);
      setLogs(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando actividad");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    queueMicrotask(load);
  }, [result]);

  if (!isAdmin) return null;

  return (
    <section
      data-testid={LOGIN_LOG.section}
      className="mt-10 border border-[#d9cfb8] bg-white p-5 shadow-sm"
    >
      <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#1c1a14]">
            <ShieldAlert size={16} className="text-[#7a5b0f]" />
            <h3 className="font-serif text-base">Actividad de inicios de sesión</h3>
          </div>
          <p className="text-[11px] text-[#5d5849] mt-0.5">
            Intentos exitosos y fallidos de los últimos 90 días. Útil para detectar
            accesos sospechosos.
          </p>
        </div>
        <button
          type="button"
          data-testid={LOGIN_LOG.refresh}
          onClick={load}
          className="inline-flex items-center gap-1 rounded border border-[#d9cfb8] px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#fbf7ee]"
        >
          <RefreshCw size={12} /> Actualizar
        </button>
      </div>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
            Resultado
          </span>
          <select
            data-testid={LOGIN_LOG.resultFilter}
            value={result}
            onChange={(e) => setResult(e.target.value)}
            className="border border-[#d9cfb8] bg-white px-3 py-1.5 text-[12px] rounded focus:border-[#c8a04a] focus:outline-none"
          >
            <option value="">Todos</option>
            <option value="success">Exitosos</option>
            <option value="fail">Fallidos</option>
          </select>
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
            Correo contiene
          </span>
          <input
            data-testid={LOGIN_LOG.emailFilter}
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="ej. gmail"
            className="border border-[#d9cfb8] bg-white px-3 py-1.5 text-[12px] rounded focus:border-[#c8a04a] focus:outline-none"
          />
        </label>
        <button
          type="button"
          onClick={load}
          className="rounded border border-[#1c2a3a] bg-[#1c2a3a] px-3 py-1.5 text-[11px] uppercase tracking-widest text-white hover:bg-[#0f1a2a]"
        >
          Filtrar
        </button>
      </div>

      <div className="overflow-x-auto border border-[#e8e1cd]">
        <table data-testid={LOGIN_LOG.table} className="w-full text-[12px]">
          <thead className="bg-[#fbf7ee] text-[#5b5240]">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-medium">Fecha y hora</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-medium">Usuario</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-medium">Resultado</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-medium">Método</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-medium">IP</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-[#8b7e5e]">Cargando...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-[#8b7e5e]">Sin registros.</td></tr>
            ) : (
              logs.map((l) => (
                <tr key={l.id} className="border-t border-[#e8e1cd] hover:bg-[#fbf7ee]">
                  <td className="px-3 py-1.5 tabular-nums text-[#5d5849] whitespace-nowrap">
                    {formatTimestamp(l.created_at)}
                  </td>
                  <td className="px-3 py-1.5 text-[#1c1a14]">{l.email}</td>
                  <td className="px-3 py-1.5">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-[10px] uppercase tracking-widest border ${
                        l.success
                          ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                          : "border-red-400 bg-red-50 text-red-800"
                      }`}
                    >
                      {l.success ? "Exitoso" : "Fallido"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-[#5d5849]">
                    <span className="inline-flex items-center gap-1">
                      {l.method === "pin" ? <Hash size={11} /> : <KeyRound size={11} />}
                      {l.method === "pin" ? "PIN (2FA)" : "Contraseña"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 tabular-nums text-[#8b7e5e]">{l.ip || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
